import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Typography,
  Box,
  IconButton,
  Card,
  CardContent,
  Grid,
  Chip,
  LinearProgress,
  alpha,
  Divider,
  TextField,
  InputAdornment,
  Button,
  Stack
} from "@mui/material";
import { DataGrid } from '@mui/x-data-grid';
import {
  ArrowBack,
  TrendingUp,
  Devices,
  Schedule,
  Speed,
  CheckCircle,
  ErrorOutline,
  Search,
  Clear,
  FilterList,
  GetApp,
  Power
} from "@mui/icons-material";
import { useGetReliabilityDataQuery, useGetReliabilitySummaryQuery } from "../analyticsApiSlice";

const ReliabilityDetailsPage = () => {
  const { summaryId } = useParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50
  });

  // Get summary data first
  const {
    data: summaryData = [],
    isLoading: summaryLoading
  } = useGetReliabilitySummaryQuery();

  // Find the specific summary
  const summary = summaryData.find(item => item.id === parseInt(summaryId));

  const {
    data: details = [],
    isLoading,
    isError,
    error
  } = useGetReliabilityDataQuery(summaryId, {
    skip: !summaryId
  });

  // SmartNode Color Scheme
  const smartNodeColors = {
    primary: '#FDB940',
    secondary: '#805088',
    success: '#26BF00',
    info: '#2E93fA',
    danger: '#e34e24',
    light: '#FFD891',
    background: '#F8F8F8'
  };

  // Filter data based on search term
  const filteredDetails = details.filter(device => {
    if (!searchTerm) return true;
    const serialMatch = device.device.serial_number.toLowerCase().includes(searchTerm.toLowerCase());
    const geyserMatch = device.geyser?.address?.toLowerCase().includes(searchTerm.toLowerCase()) || false;
    return serialMatch || geyserMatch;
  });

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const exportToCSV = () => {
    const headers = ['Serial Number', 'Geyser/Address', 'Uptime (Hrs)', 'Downtime (Hrs)', 'Uptime %', 'Message Consistency %', 'Max Power (kW)', 'Online/Offline Events'];
    const csvData = [headers];
    
    filteredDetails.forEach(device => {
      csvData.push([
        device.device.serial_number,
        device.geyser?.address || 'N/A',
        device.uptime != null ? device.uptime : 'No data',
        device.downtime != null ? device.downtime : 'No data',
        device.percent_uptime,
        device.consistent_messages,
        device.max_power != null ? (device.max_power / 1000).toFixed(2) : 'No data',
        device.online_offline_events != null ? device.online_offline_events : 'No data'
      ]);
    });
    
    const csvContent = csvData.map(row => row.join(';')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `reliability-details-${summary?.summary_date}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const getUptimeColor = (uptime) => {
    if (uptime >= 95) return smartNodeColors.success;
    if (uptime >= 85) return smartNodeColors.primary;
    return smartNodeColors.danger;
  };

  const getConsistencyColor = (consistency) => {
    if (consistency >= 90) return smartNodeColors.success;
    if (consistency >= 75) return smartNodeColors.primary;
    return smartNodeColors.danger;
  };

  // Define DataGrid columns
  const columns = [
    {
      field: 'serial_number',
      headerName: 'Serial Number',
      width: 150,
      renderCell: (params) => (
        <Chip 
          label={params.value}
          size="small"
          sx={{ 
            backgroundColor: alpha(smartNodeColors.info, 0.2),
            color: smartNodeColors.info,
            fontWeight: 'bold'
          }}
        />
      )
    },
    {
      field: 'geyser_address',
      headerName: 'Geyser / Address',
      width: 200,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ 
          fontWeight: 'medium',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {params.value || "N/A"}
        </Typography>
      )
    },
    {
      field: 'uptime',
      headerName: 'Uptime (Hrs)',
      width: 130,
      type: 'number',
      renderCell: (params) => (
        <Typography variant="body2" color={smartNodeColors.success} fontWeight="bold">
          {params.value != null ? params.value.toFixed(2) : "No data"}
        </Typography>
      )
    },
    {
      field: 'downtime',
      headerName: 'Downtime (Hrs)',
      width: 140,
      type: 'number',
      renderCell: (params) => (
        <Typography variant="body2" color={smartNodeColors.danger} fontWeight="bold">
          {params.value != null ? params.value.toFixed(2) : "No data"}
        </Typography>
      )
    },
    {
      field: 'percent_uptime',
      headerName: 'Uptime %',
      width: 120,
      type: 'number',
      renderCell: (params) => (
        <Chip 
          label={`${params.value}%`}
          size="small"
          sx={{ 
            backgroundColor: alpha(getUptimeColor(params.value), 0.2),
            color: getUptimeColor(params.value),
            fontWeight: 'bold'
          }}
        />
      )
    },
    {
      field: 'consistent_messages',
      headerName: 'Message Consistency %',
      width: 180,
      type: 'number',
      renderCell: (params) => (
        <Chip 
          label={`${params.value}%`}
          size="small"
          sx={{ 
            backgroundColor: alpha(getConsistencyColor(params.value), 0.2),
            color: getConsistencyColor(params.value),
            fontWeight: 'bold'
          }}
        />
      )
    },
    {
      field: 'max_power',
      headerName: 'Max Power (kW)',
      width: 140,
      type: 'number',
      renderCell: (params) => (
        <Typography variant="body2" color={smartNodeColors.info} fontWeight="bold">
          {params.value != null ? (params.value / 1000).toFixed(2) : "No data"}
        </Typography>
      )
    },
    {
      field: 'online_offline_events',
      headerName: 'Online/Offline Events',
      width: 180,
      type: 'number',
      renderCell: (params) => (
        <Typography variant="body2" color={smartNodeColors.secondary} fontWeight="bold">
          {params.value != null ? params.value : "No data"}
        </Typography>
      )
    }
  ];

  // Transform data for DataGrid
  const rows = filteredDetails.map((device, index) => ({
    id: device.id || index,
    serial_number: device.device.serial_number,
    geyser_address: device.geyser?.address,
    uptime: device.uptime,
    downtime: device.downtime,
    percent_uptime: device.percent_uptime,
    consistent_messages: device.consistent_messages,
    max_power: device.max_power,
    online_offline_events: device.online_offline_events
  }));

  // Show loading state
  if (isLoading || summaryLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography variant="h6" gutterBottom>Loading device details...</Typography>
        <Box sx={{ mt: 2 }}>
          <LinearProgress sx={{ width: '100%' }} />
        </Box>
      </Box>
    );
  }

  // Show error state
  if (isError) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography variant="h6" color="error">
          Error loading analytics data: {error?.data?.message || error?.message || 'Unknown error'}
        </Typography>
      </Box>
    );
  }

  if (!summary) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography variant="h6" color="error">
          Summary not found
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: 3, 
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
      minHeight: '100vh'
    }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <IconButton
            onClick={() => navigate('/analytics')}
            sx={{ 
              backgroundColor: alpha(smartNodeColors.primary, 0.2),
              '&:hover': {
                backgroundColor: alpha(smartNodeColors.primary, 0.3),
                transform: 'scale(1.1)'
              }
            }}
          >
            <ArrowBack />
          </IconButton>
          <TrendingUp sx={{ fontSize: 32, color: smartNodeColors.primary }} />
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 'bold',
              color: '#333',
              flexGrow: 1
            }}
          >
            Device Reliability Details — {new Date(summary.summary_date).toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </Typography>
          <Button
            startIcon={<GetApp />}
            variant="contained"
            size="small"
            onClick={exportToCSV}
            sx={{ 
              backgroundColor: smartNodeColors.primary,
              color: '#333',
              '&:hover': {
                backgroundColor: smartNodeColors.light
              }
            }}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* Summary Section */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h5" 
          sx={{ 
            fontWeight: 'bold',
            color: '#333',
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <Speed sx={{ color: smartNodeColors.primary }} />
          Performance Overview
        </Typography>

        <Grid container spacing={3}>
          {/* First Row - Main Metrics */}
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: `linear-gradient(135deg, ${smartNodeColors.success} 0%, #4caf50 100%)`,
              color: 'white',
              height: '100%'
            }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Devices sx={{ fontSize: 32, mb: 1 }} />
                <Typography variant="h4" fontWeight="bold">
                  {summary.total_devices}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Total Devices
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: `linear-gradient(135deg, ${smartNodeColors.primary} 0%, ${smartNodeColors.light} 100%)`,
              color: '#333',
              height: '100%'
            }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <TrendingUp sx={{ fontSize: 32, mb: 1 }} />
                <Typography variant="h4" fontWeight="bold">
                  {summary.average_percentage_uptime?.toFixed(1)}%
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Avg Uptime
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={summary.average_percentage_uptime || 0}
                  sx={{ 
                    mt: 1, 
                    height: 6, 
                    borderRadius: 3,
                    backgroundColor: 'rgba(255,255,255,0.3)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#333'
                    }
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: `linear-gradient(135deg, ${smartNodeColors.secondary} 0%, #9c27b0 100%)`,
              color: 'white',
              height: '100%'
            }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <CheckCircle sx={{ fontSize: 32, mb: 1 }} />
                <Typography variant="h4" fontWeight="bold">
                  {summary.average_consistent_messages?.toFixed(1)}%
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Message Consistency
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: `linear-gradient(135deg, ${smartNodeColors.info} 0%, #1976d2 100%)`,
              color: 'white',
              height: '100%'
            }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Power sx={{ fontSize: 32, mb: 1 }} />
                <Typography variant="h4" fontWeight="bold">
                  {summary.total_max_power ? (summary.total_max_power / 1000).toFixed(1) : '0'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Total Max Power (kW)
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Second Row - Time Metrics */}
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'white', boxShadow: 2, height: '100%' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Schedule sx={{ color: smartNodeColors.info, fontSize: 32, mb: 1 }} />
                <Typography variant="h5" fontWeight="bold" color={smartNodeColors.info}>
                  {summary.report_time_range}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Hours Range
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'white', boxShadow: 2, height: '100%' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <TrendingUp sx={{ color: smartNodeColors.success, fontSize: 32, mb: 1 }} />
                <Typography variant="h5" fontWeight="bold" color={smartNodeColors.success}>
                  {summary.average_uptime?.toFixed(1)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Avg Uptime (Hrs)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'white', boxShadow: 2, height: '100%' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <ErrorOutline sx={{ color: smartNodeColors.danger, fontSize: 32, mb: 1 }} />
                <Typography variant="h5" fontWeight="bold" color={smartNodeColors.danger}>
                  {summary.average_downtime?.toFixed(1)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Avg Downtime (Hrs)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Search and Filter Section */}
      <Box sx={{ mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <TextField
            placeholder="Search by serial number or geyser address..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: smartNodeColors.primary }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}>
                    <Clear />
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{ 
              flex: 1,
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: smartNodeColors.primary
                },
                '&.Mui-focused fieldset': {
                  borderColor: smartNodeColors.primary
                }
              }
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip 
              icon={<FilterList />}
              label={`${filteredDetails.length} of ${details.length} devices`}
              sx={{ 
                backgroundColor: alpha(smartNodeColors.primary, 0.1),
                color: smartNodeColors.primary,
                fontWeight: 'bold'
              }}
            />
          </Box>
        </Stack>
      </Box>

      {/* Device DataGrid Section */}
      <Box sx={{ width: '100%', '& .MuiDataGrid-root': { minHeight: '400px' } }}>
        <DataGrid
          rows={rows}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          autoHeight
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: alpha(smartNodeColors.primary, 0.1),
              borderBottom: `2px solid ${smartNodeColors.primary}`,
              fontWeight: 'bold',
              fontSize: '0.875rem'
            },
            '& .MuiDataGrid-cell': {
              borderBottom: `1px solid ${alpha(smartNodeColors.background, 0.5)}`,
              fontSize: '0.875rem'
            },
            '& .MuiDataGrid-row': {
              '&:nth-of-type(odd)': {
                backgroundColor: alpha(smartNodeColors.background, 0.3)
              },
              '&:hover': {
                backgroundColor: alpha(smartNodeColors.primary, 0.1)
              }
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: `2px solid ${smartNodeColors.primary}`,
              backgroundColor: alpha(smartNodeColors.background, 0.3)
            },
            '& .MuiTablePagination-root': {
              color: '#333'
            },
            '& .MuiDataGrid-columnSeparator': {
              display: 'none'
            },
            '& .MuiDataGrid-virtualScroller': {
              backgroundColor: 'white'
            }
          }}
        />
      </Box>
    </Box>
  );
};

export default ReliabilityDetailsPage;